import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { getUserToken } from "utils/helperFunctions";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";
import {
  GoalsListResponse,
  GoalItem,
  FetchGoalsParams,
  CreateGoalPayload,
  GoalReflection,
  ReflectionPayload,
  ApiResponse,
  ApiCountResponse,
  ApiPageResponse,
  ApiGoalIdResponse,
  ReflectionResponse,
  LinkableGoal,
} from "./types";
import { getGoalsParamKey, buildGoalsQueryParams } from "./helpers";

/* --------------------------------------------------------
  TAG TYPES & CACHE INVALIDATION STRATEGY
-------------------------------------------------------- */

/**
 * Cache Invalidation Strategy:
 *
 * 1. GoalsList tags:
 *    - Tagged with ['GoalsList', paramsKey] for each unique query
 *    - Also provides individual ['Goal', goalId] for each goal in the list
 *
 * 2. Individual Goal tags:
 *    - Each goal is tagged with ['Goal', goalId]
 *    - When a goal is updated/deleted, invalidate ['Goal', goalId]
 *    - This causes ALL GoalsList queries containing that goal to refetch
 *
 * 3. Reflection mutations:
 *    - When a reflection is created/updated/deleted:
 *      • Invalidate ['Goal', goalId] - this updates all lists containing that goal
 *      • Invalidate ['Reflections', goalId] - refreshes the reflections list
 *
 * This solves the nested data problem: updating a reflection automatically
 * updates all goal lists showing that goal's reflections.
 */

export const goalsApi = createApi({
  reducerPath: "goalsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${REACT_APP_API_URL}/talentInsights`,
    prepareHeaders: (headers) => {
      const token = getUserToken();
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Goal", "GoalsList", "Reflections", "LinkableGoals"],
  endpoints: (builder) => ({
    /* --------------------------------------------------------
      GOALS ENDPOINTS
    -------------------------------------------------------- */

    /**
     * Fetch paginated goals list with filters
     * Provides individual Goal tags for each goal in the response
     */
    getGoalsList: builder.query<
      GoalsListResponse,
      FetchGoalsParams & { refresh?: boolean }
    >({
      query: (params) => {
        const urlParams = buildGoalsQueryParams({
          ...params,
          page: params.page ?? 1,
        });
        return `goals?${urlParams.toString()}`;
      },
      providesTags: (result, _, params) => {
        const paramsKey = getGoalsParamKey(params);

        if (!result?.success || !result?.data?.data) {
          return [{ type: "GoalsList", id: paramsKey }];
        }

        return [
          { type: "GoalsList", id: paramsKey },
          ...result.data.data.map((goal) => ({
            type: "Goal" as const,
            id: goal.goalId,
          })),
        ];
      },
      forceRefetch: ({ currentArg }) => currentArg?.refresh === true,
    }),

    /**
     * Create a new goal
     * Invalidates all GoalsList queries to show the new goal
     */
    createGoal: builder.mutation<void, CreateGoalPayload>({
      query: (payload) => ({
        url: "goals",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success(
            "Goal created successfully!",
            successNotificationOptions
          );
        } catch {
          toast.error("Failed to create goal");
        }
      },
      invalidatesTags: [{ type: "GoalsList" }],
    }),

    /**
     * Update an existing goal
     * Invalidates the specific Goal tag, which auto-refetches all lists containing it
     */
    updateGoal: builder.mutation<
      void,
      { goalId: number; payload: Partial<GoalItem> }
    >({
      query: ({ goalId, payload }) => ({
        url: `goals/${goalId}`,
        method: "PUT",
        body: payload,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success(
            "Goal updated successfully!",
            successNotificationOptions
          );
        } catch {
          toast.error("Failed to update goal");
        }
      },
      invalidatesTags: (_, __, { goalId, payload }) => {
        // If archiving/unarchiving, invalidate all lists since the goal moves between them
        if ("archived" in payload) {
          return [{ type: "GoalsList" }];
        }
        // For other updates, only invalidate the specific goal
        return [{ type: "Goal", id: goalId }];
      },
    }),

    /**
     * Delete a goal
     * Invalidates the specific Goal tag, which auto-refetches all lists containing it
     */
    deleteGoal: builder.mutation<void, { goalId: number }>({
      query: ({ goalId }) => ({
        url: `goals/${goalId}`,
        method: "DELETE",
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success(
            "Goal deleted successfully!",
            successNotificationOptions
          );
        } catch {
          toast.error("Failed to delete goal");
        }
      },
      invalidatesTags: (_, __, { goalId }) => [{ type: "Goal", id: goalId }],
    }),

    /**
     * Fetch linkable goals for linking functionality
     */
    getLinkableGoals: builder.query<
      LinkableGoal[],
      { userAccountId?: number } | void
    >({
      query: (params) => {
        const urlParams = new URLSearchParams();
        if (params && "userAccountId" in params && params.userAccountId) {
          urlParams.append("userAccountId", String(params.userAccountId));
        }
        return `goals/linkable?${urlParams.toString()}`;
      },
      transformResponse: (response: ApiResponse<LinkableGoal[]>) =>
        response?.data ?? [],
      providesTags: ["LinkableGoals"],
    }),

    /**
     * Get count of linked goals for a specific goal
     */
    getLinkedGoalsCount: builder.query<number, number>({
      query: (goalId) => `goals/${goalId}/linked-count`,
      transformResponse: (response: ApiCountResponse) => response?.count ?? 0,
      providesTags: (_, __, goalId) => [{ type: "Goal", id: goalId }],
    }),

    /**
     * Get the page number where a specific goal appears (for pagination jump)
     * Now accepts filter params to ensure page calculation matches filtered results
     */
    getGoalPage: builder.query<
      number,
      { goalId: number; params: FetchGoalsParams }
    >({
      query: ({ goalId, params }) => {
        const urlParams = buildGoalsQueryParams(params);
        return `goals/${goalId}/page?${urlParams.toString()}`;
      },
      transformResponse: (response: ApiPageResponse) => {
        if (!response?.success || !response?.page) {
          throw new Error("Failed to fetch goal page");
        }
        return response.page;
      },
    }),

    /**
     * Get the first past unreflected goal
     */
    getFirstPastUnreflectedGoal: builder.query<number | null, void>({
      query: () => "goals/first-past-unreflected",
      transformResponse: (response: ApiGoalIdResponse) => {
        if (!response?.success) {
          throw new Error("Failed to fetch past goal");
        }
        return response.goalId ?? null;
      },
      providesTags: ["Goal"],
    }),

    /* --------------------------------------------------------
      REFLECTION ENDPOINTS
    -------------------------------------------------------- */

    /**
     * Fetch all reflections for a specific goal
     */
    getReflectionsByGoalId: builder.query<GoalReflection[], { goalId: number }>(
      {
        query: ({ goalId }) => `reflections/${goalId}`,
        transformResponse: (response: ApiResponse<GoalReflection[]>) =>
          response?.data ?? [],
        providesTags: (_, __, { goalId }) => [
          { type: "Reflections", id: goalId },
        ],
      }
    ),

    /**
     * Create a new reflection for a goal
     *
     * KEY FEATURE: Invalidates the parent Goal tag, causing all GoalsList
     * queries containing that goal to automatically refetch and show the
     * new reflection in the nested data structure.
     */
    createReflection: builder.mutation<ReflectionResponse, ReflectionPayload>({
      query: (payload) => ({
        url: `goals/${payload.goalId}/reflections`,
        method: "POST",
        body: {
          progressPercent: payload.progressPercent,
          status: payload.status,
          comments: payload.comments,
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Reflection added successfully!");
        } catch {
          toast.error("Failed to add reflection");
        }
      },
      invalidatesTags: (_, __, { goalId }) => [
        { type: "Goal", id: goalId },
        { type: "Reflections", id: goalId },
      ],
    }),

    /**
     * Update an existing reflection
     *
     * KEY FEATURE: This is the core solution to the cache coherency problem.
     * By invalidating ['Goal', goalId], ALL cached GoalsList queries that
     * contain this goal will automatically refetch. This ensures that any
     * goal cards showing this goal's reflections will immediately display
     * the updated reflection data without manual intervention.
     *
     * Example:
     * - User has goals list on page 1 and page 3 open
     * - Both pages contain goal #10 with reflections
     * - User updates a reflection for goal #10
     * - This invalidates ['Goal', 10]
     * - Both page 1 and page 3 queries automatically refetch
     * - Both pages now show the updated reflection data
     */
    updateReflection: builder.mutation<
      ReflectionResponse,
      { reflectionId: number; payload: ReflectionPayload }
    >({
      query: ({ reflectionId, payload }) => ({
        url: `reflections/${reflectionId}`,
        method: "PUT",
        body: payload,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Reflection updated successfully!", {
            ...successNotificationOptions,
          });
        } catch {
          toast.error("Failed to update reflection");
        }
      },
      invalidatesTags: (_, __, { payload }) => [
        { type: "Goal", id: payload.goalId },
        { type: "Reflections", id: payload.goalId },
      ],
    }),

    /**
     * Delete a reflection
     *
     * Invalidates the parent Goal tag to update all goal lists
     */
    deleteReflection: builder.mutation<
      ApiResponse<void>,
      { reflectionId: number; goalId: number }
    >({
      query: ({ reflectionId }) => ({
        url: `reflections/${reflectionId}`,
        method: "DELETE",
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Reflection deleted");
        } catch {
          toast.error("Failed to delete reflection");
        }
      },
      invalidatesTags: (_, __, { goalId }) => [
        { type: "Goal", id: goalId },
        { type: "Reflections", id: goalId },
      ],
    }),
  }),
});

/* --------------------------------------------------------
  EXPORT HOOKS
-------------------------------------------------------- */

export const {
  // Goal queries
  useGetGoalsListQuery,
  useLazyGetGoalsListQuery,
  useGetLinkableGoalsQuery,
  useLazyGetLinkableGoalsQuery,
  useGetLinkedGoalsCountQuery,
  useLazyGetLinkedGoalsCountQuery,
  useGetGoalPageQuery,
  useLazyGetGoalPageQuery,
  useGetFirstPastUnreflectedGoalQuery,
  useLazyGetFirstPastUnreflectedGoalQuery,

  // Goal mutations
  useCreateGoalMutation,
  useUpdateGoalMutation,
  useDeleteGoalMutation,

  // Reflection queries
  useGetReflectionsByGoalIdQuery,
  useLazyGetReflectionsByGoalIdQuery,

  // Reflection mutations
  useCreateReflectionMutation,
  useUpdateReflectionMutation,
  useDeleteReflectionMutation,
} = goalsApi;
